import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supportTicketService from '../../services/supportTicketService';
import chatService, {
  CHAT_MESSAGE_EVENT,
  CHAT_UNREAD_REFRESH_EVENT,
} from '../../services/chatService';
import { triggerShowAssignedReviews } from '../AssignedReviewsModal/AssignedReviewsModal';
import { getChatRoleBadgeClass, getChatRoleDisplay } from '../../utils/chatPermissions';
import { formatTicketDate, isNotificationUnread, TICKET_NOTIFICATIONS_REFRESH_EVENT } from './supportTicketUtils';
import './SupportTicketPanel.css';

const sortNotificationsNewestFirst = (items = []) =>
  [...items].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

const assignedReviewTitle = (notification) => {
  const stored = String(notification.ticket_title || notification.title || '').trim();
  if (notification.vital_type === 'pcm_care_plan') {
    if (!stored || /abnormal reading/i.test(stored)) return 'Care plan to review';
    return stored;
  }
  return stored || 'Abnormal Reading Assigned';
};

const TicketNotificationBell = ({ onOpenTickets, onCountChange, userId, includeChat = false }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [ticketCount, setTicketCount] = useState(0);
  const [chatCount, setChatCount] = useState(0);
  const [ticketNotifications, setTicketNotifications] = useState([]);
  const [chatNotifications, setChatNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  const totalCount = ticketCount + (includeChat ? chatCount : 0);

  const refreshNotifications = useCallback(async () => {
    try {
      const ticketPromise = Promise.all([
        supportTicketService.getUnreadCount(),
        supportTicketService.getNotifications(true),
      ]);
      const chatPromise =
        includeChat && userId
          ? Promise.all([
              chatService.getUnreadCount(userId),
              chatService.getNotifications(userId, true),
            ])
          : Promise.resolve([{ data: { count: 0 } }, { data: [] }]);

      const [[ticketCountResult, ticketListResult], [chatCountResult, chatListResult]] =
        await Promise.all([ticketPromise, chatPromise]);

      const nextTicketCount = Number(ticketCountResult?.data?.count || 0);
      const nextChatCount = Number(chatCountResult?.data?.count || 0);
      setTicketCount(nextTicketCount);
      setChatCount(nextChatCount);
      setTicketNotifications(sortNotificationsNewestFirst(ticketListResult?.data || []));
      setChatNotifications(sortNotificationsNewestFirst(chatListResult?.data || []));
      onCountChange?.(nextTicketCount + nextChatCount);
    } catch {
      // Silent fail for background polling
    }
  }, [includeChat, onCountChange, userId]);

  useEffect(() => {
    refreshNotifications();
    const intervalId = window.setInterval(refreshNotifications, 60000);
    const onTicketRefresh = () => refreshNotifications();
    window.addEventListener(TICKET_NOTIFICATIONS_REFRESH_EVENT, onTicketRefresh);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener(TICKET_NOTIFICATIONS_REFRESH_EVENT, onTicketRefresh);
    };
  }, [refreshNotifications]);

  useEffect(() => {
    if (!includeChat || !userId) return undefined;
    const onRefresh = () => refreshNotifications();
    const onIncomingMessage = (event) => {
      const msg = event.detail;
      if (Number(msg?.to_user_id) === Number(userId) && !Number(msg?.is_read)) {
        setChatCount((prev) => prev + 1);
      }
      refreshNotifications();
    };
    window.addEventListener(CHAT_MESSAGE_EVENT, onIncomingMessage);
    window.addEventListener(CHAT_UNREAD_REFRESH_EVENT, onRefresh);
    return () => {
      window.removeEventListener(CHAT_MESSAGE_EVENT, onIncomingMessage);
      window.removeEventListener(CHAT_UNREAD_REFRESH_EVENT, onRefresh);
    };
  }, [includeChat, refreshNotifications, userId]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  const handleToggle = async () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen) {
      setLoading(true);
      try {
        const ticketListResult = await supportTicketService.getNotifications(false);
        setTicketNotifications(sortNotificationsNewestFirst(ticketListResult?.data || []));
        if (includeChat && userId) {
          const chatListResult = await chatService.getNotifications(userId, false);
          setChatNotifications(sortNotificationsNewestFirst(chatListResult?.data || []));
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTicketClick = async (notification) => {
    if (isNotificationUnread(notification)) {
      await supportTicketService.markNotificationRead(notification.id);
    }
    setOpen(false);
    refreshNotifications();
    onOpenTickets?.();
  };

  const handleAssignedReviewClick = async (notification) => {
    if (isNotificationUnread(notification)) {
      await supportTicketService.markNotificationRead(notification.id);
    }
    setOpen(false);
    refreshNotifications();
    if (notification.vital_type === 'pcm_care_plan' && notification.practice_id && notification.patient_id) {
      navigate(`/practices/${notification.practice_id}/patients/${notification.patient_id}?program=PCM`, {
        state: { activeProgram: 'PCM' }
      });
      return;
    }
    if (notification.practice_id && notification.patient_id) {
      navigate(`/practices/${notification.practice_id}?tab=patient&patientId=${notification.patient_id}`);
    } else {
      triggerShowAssignedReviews();
    }
  };

  const handleChatClick = (notification) => {
    const fromUserId = notification?.from_user_id;
    setOpen(false);
    if (fromUserId != null) {
      navigate(`/chat?with=${fromUserId}`);
    } else {
      navigate('/chat');
    }
    refreshNotifications();
  };

  const handleMarkAllRead = async () => {
    const tasks = [supportTicketService.markAllNotificationsRead()];
    if (includeChat && userId && chatCount > 0) {
      tasks.push(chatService.markAllNotificationsRead(userId));
    }
    await Promise.all(tasks);
    refreshNotifications();
  };

  const hasAnyItems = ticketNotifications.length > 0 || chatNotifications.length > 0;

  const mergedNotifications = useMemo(() => {
    const items = [
      ...(includeChat
        ? chatNotifications.map((notification) => ({ kind: 'chat', notification }))
        : []),
      ...ticketNotifications.map((notification) => ({
        kind: notification.kind === 'assigned_review' ? 'assigned_review' : 'ticket',
        notification
      })),
    ];
    return items.sort(
      (a, b) =>
        new Date(b.notification.created_at).getTime() -
        new Date(a.notification.created_at).getTime()
    );
  }, [chatNotifications, includeChat, ticketNotifications]);

  const renderNotificationItem = ({ kind, notification }) => {
    const unread = isNotificationUnread(notification);
    const itemClass = 'header-ticket-notification-item';

    if (kind === 'assigned_review' || notification.kind === 'assigned_review') {
      return (
        <button
          key={`review-${notification.id}`}
          type="button"
          className={itemClass}
          onClick={() => handleAssignedReviewClick(notification)}
        >
          <span className="header-notification-item-top">
            <span
              className="header-notification-kind"
              style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', fontWeight: 600 }}
            >
              {notification.vital_type === 'pcm_care_plan' ? 'Care plan' : 'Assigned Review'}
            </span>
            {unread ? (
              <span className="header-notification-unread-dot" aria-label="Unread" />
            ) : (
              <span className="header-notification-unread-dot placeholder" aria-hidden="true" />
            )}
          </span>
          <div className="header-chat-notification-meta" style={{ marginTop: '4px' }}>
            <strong style={{ color: '#0f172a' }}>{assignedReviewTitle(notification)}</strong>
          </div>
          <p style={{ margin: '4px 0', fontSize: '12px', color: '#334155' }}>{notification.message}</p>
          <small>{formatTicketDate(notification.created_at)}</small>
        </button>
      );
    }

    if (kind === 'chat') {
      return (
        <button
          key={`chat-${notification.id}`}
          type="button"
          className={itemClass}
          onClick={() => handleChatClick(notification)}
        >
          <span className="header-notification-item-top">
            <span className="header-notification-kind">Message</span>
            {unread ? (
              <span className="header-notification-unread-dot" aria-label="Unread" />
            ) : (
              <span className="header-notification-unread-dot placeholder" aria-hidden="true" />
            )}
          </span>
          <div className="header-chat-notification-meta">
            <strong>{notification.from_user_name || 'Someone'}</strong>
            <span
              className={`chat-role-badge chat-role-badge--compact ${getChatRoleBadgeClass(notification.from_user_role_id)}`}
            >
              {getChatRoleDisplay(
                notification.from_user_role_id,
                notification.from_user_role_name
              )}
            </span>
          </div>
          <p className="header-chat-notification-preview">
            {(notification.message || 'New message').slice(0, 120)}
          </p>
          <small>{formatTicketDate(notification.created_at)}</small>
        </button>
      );
    }

    return (
      <button
        key={`ticket-${notification.id}`}
        type="button"
        className={itemClass}
        onClick={() => handleTicketClick(notification)}
      >
        <span className="header-notification-item-top">
          <span className="header-notification-kind">Support ticket</span>
          {unread ? (
            <span className="header-notification-unread-dot" aria-label="Unread" />
          ) : (
            <span className="header-notification-unread-dot placeholder" aria-hidden="true" />
          )}
        </span>
        <p>{notification.message}</p>
        <small>{formatTicketDate(notification.created_at)}</small>
      </button>
    );
  };

  return (
    <div className="header-ticket-notifications" ref={containerRef}>
      <button
        type="button"
        className="header-icon-btn"
        aria-label="Notifications"
        title={
          includeChat && chatCount > 0
            ? `Notifications (${chatCount} unread message${chatCount === 1 ? '' : 's'})`
            : 'Notifications'
        }
        onClick={handleToggle}
      >
        <i className="fa fa-bell" aria-hidden="true"></i>
        {totalCount > 0 && (
          <span className="header-notification-badge">{totalCount > 99 ? '99+' : totalCount}</span>
        )}
      </button>

      {open && (
        <div className="header-ticket-notifications-panel">
          <div className="header-ticket-notifications-head">
            <h3>Notifications</h3>
            {totalCount > 0 && (
              <button type="button" className="support-ticket-action-btn secondary" onClick={handleMarkAllRead}>
                Mark all read
              </button>
            )}
          </div>
          <div className="header-ticket-notifications-list">
            {loading ? (
              <div className="header-ticket-notifications-empty">Loading…</div>
            ) : !hasAnyItems ? (
              <div className="header-ticket-notifications-empty">No notifications.</div>
            ) : (
              mergedNotifications.map(renderNotificationItem)
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketNotificationBell;
